import React, { useCallback, useContext, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import * as R from 'ramda';

import WorkOrdersContext, {
  WorkOrdersInputActionTypes,
} from '@atom/components/workOrders/WorkOrdersContext';
import client from '@atom/graph/client';
import { GET_USERS } from '@atom/graph/user';
import { GET_USER_GROUPS } from '@atom/graph/userGroup';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Autocomplete, Avatar, Chip, Icon, List, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import { TaskUserStatus } from '@atom/types/task';
import {
  GroupFilterOption,
  UserDetail,
  UserFilterOption,
  UsersConnection,
  UsersConnectionInput,
} from '@atom/types/user';
import {
  UserGroup,
  UserGroupsConnection,
  UserGroupsConnectionInput,
} from '@atom/types/userGroups';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getColorFromColorId } from '@atom/utilities/colorUtilities';
import {
  getMeUserOption,
  getMyGroupsOption,
  getUserFullName,
  MY_GROUPS_ID,
} from '@atom/utilities/userUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

const { ListItemText } = List;

type DebouncedSearch = (name: string, ids?: string[]) => void;
type UserOrGroup =
  | UserDetail
  | UserGroup
  | UserFilterOption
  | GroupFilterOption;

const DEBOUNCE_TIME = 300;
const MIN_CHAR = 3;
const SEARCH_LIMIT = 250;
const SEARCH_PAGE = 1;
const UNASSIGNED_USER_ID = 'unassigned';
const UNASSIGNED_USER: UserDetail = {
  baseLocation: '',
  budgets: [],
  certifications: [],
  createdDate: 0,
  department: '',
  email: '',
  employeeId: '',
  firstName: 'Unassigned',
  id: UNASSIGNED_USER_ID,
  inheritedRoles: [],
  isActive: true,
  isVerified: true,
  lastName: '',
  level: 1,
  location: { type: 'Point', coordinates: [0, 0] },
  notifications: [],
  photoUrl: '',
  rate: 0,
  role: 'Admin',
  roles: [],
  status: TaskUserStatus.ASSIGNED,
  timeEntryApproverInfo: [],
  timezoneLocation: '',
  title: '',
  phoneNumber: '',
  updatedDate: 0,
  preferences: {},
};

const styles = {
  avatar: {
    marginRight: '0.5rem',
  },
  chip: {
    marginTop: '5px',
    marginRight: '5px',
    backgroundColor: colors.neutral.ash,
    borderRadius: '20px',
  },
};

const isUser = (userOrGroup: UserOrGroup): userOrGroup is UserDetail => {
  return userOrGroup.hasOwnProperty('firstName');
};

const getUserAvatar = (user: UserDetail): React.ReactNode => (
  <Avatar style={styles.avatar} src={user.photoUrl} alt={user.firstName} />
);

const getUserGroupAvatar = (group: UserGroup): React.ReactNode => (
  <Avatar
    style={{
      ...styles.avatar,
      // Design bg color is unavailable in utility method, hardcoding here instead
      background:
        group.id === MY_GROUPS_ID
          ? colors.neutral.gray
          : getColorFromColorId(group.colorId || 0),
    }}
    alt={group.name}
  >
    <Icon style={{ fontSize: 20 }} color={colors.neutral.white}>
      people
    </Icon>
  </Avatar>
);

const WorkOrdersAssigneeFilter = () => {
  const { workOrdersInputCart, dispatch } = useContext(WorkOrdersContext);
  const userProfile = useUserProfile();
  const ME_USER_OPTION = getMeUserOption(userProfile);
  const MY_GROUPS_OPTION = getMyGroupsOption();
  const [query, setQuery] = useState<string>('');
  const [loadingSearch, setLoadingSearch] = useState<boolean>(false);
  const [loadingPreselected, setLoadingPreselected] = useState<boolean>(false);
  const [results, setResults] = useState<UserOrGroup[]>([]);
  const [selected, setSelected] = useState<UserOrGroup[]>([]);
  const { filtersDisabled } = useContext(WorkOrdersContext);

  const getUsersAndGroups = async (
    name: string = '',
    userIds: string[] = [],
    allUserGroupIds: string[] = [],
  ) => {
    const hasIds = userIds?.length || allUserGroupIds?.length;

    if (hasIds) {
      setLoadingPreselected(true);
    } else {
      setLoadingSearch(true);
    }

    const hasMyGroups = allUserGroupIds.includes(MY_GROUPS_ID);

    // If user selects only my groups, we must skip the call
    //   and resolve userGroups containing only the MY_GROUPS option
    const onlyMyGroups = hasMyGroups && allUserGroupIds.length === 1;

    // Ensure we don't send "MY_GROUPS" to GET_USER_GROUPS
    const userGroupIds = hasMyGroups
      ? R.without([MY_GROUPS_ID], allUserGroupIds)
      : allUserGroupIds;

    const [users, userGroups] = await Promise.all([
      ...(name || userIds?.length
        ? [
            client
              .query<
                { users: UsersConnection },
                { input: UsersConnectionInput }
              >({
                query: GET_USERS,
                variables: {
                  input: R.reject(isNilOrEmpty, {
                    limit: SEARCH_LIMIT,
                    page: SEARCH_PAGE,
                    name,
                    showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
                    ...(userIds.length && { ids: userIds }),
                  }),
                },
              })
              .then(({ data }) => data.users.users),
          ]
        : [Promise.resolve([])]),
      ...((name || allUserGroupIds?.length) && !onlyMyGroups
        ? [
            client
              .query<
                { userGroups: UserGroupsConnection },
                { input: UserGroupsConnectionInput }
              >({
                query: GET_USER_GROUPS,
                variables: {
                  // @ts-ignore
                  input: R.reject(isNilOrEmpty, {
                    limit: SEARCH_LIMIT,
                    page: SEARCH_PAGE,
                    showAdmin: hasRolePermissions(ROLE_SETS.ADMIN),
                    name,
                    ...(userGroupIds.length && { ids: userGroupIds }),
                  }),
                },
              })
              .then(({ data }) => {
                // Reattach My Groups option if it was present
                return hasMyGroups
                  ? [...data.userGroups.userGroups, MY_GROUPS_OPTION]
                  : data.userGroups.userGroups;
              }),
          ]
        : [Promise.resolve(onlyMyGroups ? [MY_GROUPS_OPTION] : [])]),
    ]);

    const unassigned = R.includes(UNASSIGNED_USER_ID, userIds || [])
      ? [UNASSIGNED_USER]
      : [];

    const allOptions = [...unassigned, ...users, ...userGroups];

    if (hasIds) {
      setSelected(allOptions);
      setLoadingPreselected(false);
    } else {
      setResults(allOptions);
      setLoadingSearch(false);
    }
  };

  const getUsersAndGroupsDebounced = useCallback<DebouncedSearch>(
    debounce(getUsersAndGroups, DEBOUNCE_TIME),
    [],
  );

  useEffect(() => {
    if (query.length >= MIN_CHAR) {
      getUsersAndGroupsDebounced(query);
    } else {
      const baseOptions = [ME_USER_OPTION, UNASSIGNED_USER, MY_GROUPS_OPTION];
      setResults(baseOptions);
    }
  }, [query]);

  useEffect(() => {
    const hasIds =
      workOrdersInputCart.userIds?.length ||
      workOrdersInputCart.groupIds?.length;

    if (hasIds) {
      getUsersAndGroups(
        null,
        workOrdersInputCart.userIds || [],
        workOrdersInputCart.groupIds || [],
      );
    }

    if (!hasIds && selected.length) {
      setSelected([]);
    }
  }, [workOrdersInputCart.userIds, workOrdersInputCart.groupIds]);

  const handleChange = (allValues: UserOrGroup[]) => {
    // If user selects my groups while any of their is already selected,
    // - remove those groups from selected values
    const hasMyGroups = allValues.some(({ id }) => id === MY_GROUPS_ID);
    const values = hasMyGroups
      ? allValues.filter(({ id }) => !userProfile.userGroupIds.includes(id))
      : allValues;

    setSelected(values);

    const { userIds, groupIds } = values.reduce(
      (acc, userOrGroup) => {
        return isUser(userOrGroup)
          ? {
              ...acc,
              userIds: [...acc.userIds, userOrGroup.id],
            }
          : {
              ...acc,
              groupIds: [...acc.groupIds, userOrGroup.id],
            };
      },
      { userIds: [], groupIds: [] },
    );

    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
      data: {
        property: 'userIds',
        value: userIds,
      },
    });

    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
      data: {
        property: 'groupIds',
        value: groupIds,
      },
    });
  };

  const handleDeselect = (id: string) => {
    setSelected(state => state.filter(user => user.id !== id));

    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
      data: {
        property: 'userIds',
        value:
          workOrdersInputCart?.userIds?.filter(userId => userId !== id) ?? [],
      },
    });

    dispatch({
      type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY,
      data: {
        property: 'groupIds',
        value:
          workOrdersInputCart?.groupIds?.filter(groupId => groupId !== id) ??
          [],
      },
    });
  };

  const getOptionDisabled = userOrGroup => {
    const isInMyGroupsSelected =
      selected.some(({ id }) => id === MY_GROUPS_ID) &&
      userProfile.userGroupIds.includes(userOrGroup.id);
    return (
      isInMyGroupsSelected || selected.some(({ id }) => id === userOrGroup.id)
    );
  };

  return (
    <Autocomplete
      multiple
      label="Assigned To"
      options={results}
      loading={loadingSearch}
      disabled={loadingPreselected || filtersDisabled}
      inputValue={query}
      onInputChange={(event, val) => setQuery(val || '')}
      getOptionLabel={userOrGroup =>
        isUser(userOrGroup) ? getUserFullName(userOrGroup) : userOrGroup.name
      }
      placeholder={!selected.length ? 'Search for users or groups' : ''}
      value={selected}
      onChange={(event, values: UserGroup[]) => handleChange(values)}
      getOptionDisabled={getOptionDisabled}
      filterOptions={(options, state) => options}
      renderOption={(optionProps, userOrGroup) => (
        <li key={userOrGroup.id} {...optionProps}>
          {isUser(userOrGroup)
            ? getUserAvatar(userOrGroup)
            : getUserGroupAvatar(userOrGroup)}
          <ListItemText
            primary={
              isUser(userOrGroup)
                ? getUserFullName(userOrGroup)
                : userOrGroup.name
            }
            secondary={
              isUser(userOrGroup)
                ? userOrGroup.email
                : userOrGroup.groupPath.join(' / ')
            }
          />
        </li>
      )}
      renderValue={usersOrGroups => {
        return usersOrGroups.map(userOrGroup => (
          <Chip
            key={userOrGroup.id}
            style={styles.chip}
            // @ts-ignore
            avatar={
              isUser(userOrGroup)
                ? getUserAvatar(userOrGroup)
                : getUserGroupAvatar(userOrGroup)
            }
            label={
              isUser(userOrGroup)
                ? getUserFullName(userOrGroup)
                : userOrGroup.name
            }
            onDelete={() => handleDeselect(userOrGroup.id)}
          />
        ));
      }}
      groupBy={userOrGroup => (isUser(userOrGroup) ? 'Users' : 'Groups')}
      endAdornment={loadingPreselected && <Progress size={20} />}
    />
  );
};

export default WorkOrdersAssigneeFilter;
